import './MinicartProduct.scss';

import cn from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import Dotdotdot from 'react-dotdotdot';
import { useTranslation } from 'react-i18next';

import Picture from '@/common/components/atoms/Picture/Picture';
import ButtonLoading from '@/common/components/molecules/ButtonLoading/ButtonLoading';
import Quantity from '@/common/components/molecules/Quantity/Quantity';
import getImageBySize from '@/common/helpers/getImageBySize';
import useStocks from '@/common/hooks/stocks/useStocks';
import { ReactComponent as DeleteIcon } from '@/ebat/assets/icons/delete-minicart.svg';
import ProductPrice from '@/ebat/components/blocks/Common/Product/ProductPrice/ProductPrice';
import { PRODUCTS_TYPES_FORMATS } from '@/ebat/constants';

import {
  MiniCartProductDefaultProps,
  MiniCartProductPropTypes,
} from './MiniCartProduct.proptypes';

const getFormattedPriceWithQuantity = (price, quantity) => {
  if (
    price === undefined ||
    price === null ||
    quantity === undefined ||
    quantity === null
  ) {
    return null;
  }
  const total = price * quantity;
  if (Math.floor(total) === total) {
    return total;
  }

  return Math.round(total);
};

const MiniCartProduct = ({
  className,
  cart,
  product,
  onQuantityChange,
  isLoadingDelete,
  onDeleteCartItem,
}) => {
  const { t } = useTranslation();
  const { getTotalStock } = useStocks(
    product?.product?.price_and_stock?.stocks,
  );

  const quantityFieldRef = useRef(null);
  const [isQuantityChanged, setIsQuantityChanged] = useState(false);

  useEffect(() => {
    const cartItem = cart?.items.find((item) => item.id === product.id);
    if (cartItem?.quantity !== product?.quantity) {
      setIsQuantityChanged(true);
    } else {
      setIsQuantityChanged(false);
    }
  }, [cart, product]);

  const handleChange = (newQuantity) => {
    onQuantityChange(product.id, newQuantity.quantity);
  };

  const isClearance = () =>
    product?.product?.flags.filter((flag) => flag.slug === 'clearance').length >
    0;

  const getPrice = () => {
    if (isQuantityChanged) {
      return null;
    }
    if (
      product?.discount_price !== undefined &&
      product?.discount_price !== null &&
      product.discount_price > 0
    ) {
      return getFormattedPriceWithQuantity(product?.price, product?.quantity);
    }

    return product?.line_subtotal ?? null;
  };

  const getDiscountPrice = () => {
    if (isQuantityChanged) {
      return null;
    }
    if (
      product?.discount_price !== undefined &&
      product?.discount_price !== null &&
      product.discount_price > 0
    ) {
      return product?.line_subtotal ?? null;
    }

    return null;
  };

  return (
    <div
      className={cn(className, 'mini-cart-product', {
        'mini-cart-product__is-not-salable': !product?.is_salable,
      })}
    >
      <div className="mini-cart-product__product">
        <Picture
          alt={product?.product?.image?.alt || product?.product?.name}
          className="product__image"
          src={
            product?.product?.image?.url
              ? getImageBySize(
                  product?.product?.image.url,
                  PRODUCTS_TYPES_FORMATS.LIST,
                )
              : ''
          }
        />
        <div className="product__description">
          <span className="description__title">
            <Dotdotdot clamp={3}>
              {product?.product ? product?.product?.name : product?.label}
            </Dotdotdot>
          </span>
          <span className="description__code">
            {t('common:entities:product.sku')}
            {product?.sku}
          </span>
          <span className="description__ref">
            {t('common:entities:product.manufacturer_sku')}
            {product?.supplier_reference || product?.supplier_code}
          </span>
        </div>
      </div>
      <div className="mini-cart-product__numbers">
        <>
          <Quantity
            className="numbers__quantity"
            id={product?.id}
            isLoading={isLoadingDelete}
            max={isClearance() ? getTotalStock() : undefined}
            onChange={handleChange}
            quantity={product.quantity}
            ref={quantityFieldRef}
            salesPackaging={product?.product?.price_and_stock?.sales_packaging}
            splitPackaging={product?.product?.price_and_stock?.split_packaging}
            unit={product?.product?.price_and_stock?.sale_unit}
            disabled={!product?.is_salable}
          />

          <ProductPrice
            className={cn('numbers__prices', {
              'numbers__prices-saleunit':
                product?.product?.price_and_stock?.sale_unit,
            })}
            context="product-customer-price"
            label={t('ebat:entities:mini-cart.ht')}
            price={getPrice()}
            discountPrice={getDiscountPrice()}
            weeeAmount={product?.product?.price_and_stock?.price?.weee_amount}
          />
          <ButtonLoading
            buttonSize="small"
            withIcon
            variant="tertiary"
            isLoading={isLoadingDelete}
            className={cn('mini-cart-product__button-delete', {
              'mini-cart-product__button-delete-saleunit':
                product?.product?.price_and_stock?.sale_unit,
            })}
          >
            <DeleteIcon
              className="mini-cart-product__delete"
              onClick={(e) => {
                e.preventDefault();
                onDeleteCartItem({ id: product?.id });
              }}
            />
          </ButtonLoading>
        </>
      </div>
      {!product?.is_salable && (
        <div className="mini-cart-product__salable">
          <span>{t('ebat:entities:mini-cart.salable')}</span>
        </div>
      )}
    </div>
  );
};

MiniCartProduct.defaultProps = {
  ...MiniCartProductDefaultProps,
};

MiniCartProduct.propTypes = {
  ...MiniCartProductPropTypes,
};

export default MiniCartProduct;
